import {
  JSONPatchDocument,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { socService } from "../services";
import {
  ActionPayload,
  ADD_CASE_NOTE,
  ADD_EVENT,
  ASSIGN_ITEM,
  closedSoc,
  CLOSE_ITEM,
  COMPLETE_EVENT,
  loadedItem,
  loadedItems,
  LOADING_ITEM,
  LOADING_ITEMS,
  REOPEN_ITEM,
  RESUBMIT_ITEM,
  savedItem,
  SENDING_ITEM,
  SENDING_MESSAGE,
  SENDING_MULTIPLE_MESSAGE,
  SEND_INTERNAL_MESSAGE,
  sentItem,
  sentMultipleMessage,
  UNASSIGN_ITEM,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
} from "./actions";
import { authSelector } from "./selectors";
import { SocItem } from "../domain/SocItem";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { SocMessageFilters } from "../domain/SocMessageFilters";

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchSendingSaga() {
  yield takeEvery(SENDING_ITEM, sendItemSaga);
}

function* watchSendInternalMessageSaga() {
  yield takeLatest(SEND_INTERNAL_MESSAGE, sendInternalMessage);
}

function* watchAssignSaga() {
  yield takeLatest(ASSIGN_ITEM, assignItemSaga);
}

function* watchUnassignSaga() {
  yield takeLatest(UNASSIGN_ITEM, unassignItemSaga);
}

function* watchAddCaseNoteSaga() {
  yield takeLatest(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchCloseSocSaga() {
  yield takeLatest(CLOSE_ITEM, closeSocSaga);
}

function* watchReOpenSocSaga() {
  yield takeLatest(REOPEN_ITEM, reopenSocSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}

function* watchSendMultipleMessageSaga() {
  yield takeEvery(SENDING_MULTIPLE_MESSAGE, sendMultipleMessageSaga);
}

function* watchEventSaga() {
  yield takeLatest(ADD_EVENT, addEventSaga);
}

function* watchCompleteEventSaga() {
  yield takeLatest(COMPLETE_EVENT, completeEventSaga);
}

function* watchEnableResubmitSaga() {
  yield takeLatest(RESUBMIT_ITEM, enableResubmitSaga)
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<SocItem> = yield call(
    [socService, socService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const item: SocItem = yield call(
    [socService, socService.get],
    payload,
    {
      token: auth.token,
    }
  );
  yield put(loadedItem(item));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, patchDocument } = payload as {
    id: string;
    patchDocument: JSONPatchDocument;
  };

  const validatedResponse: ValidatedResponse<SocItem> = yield call(
    [socService, socService.update],
    id,
    patchDocument,
    {
      token: auth.token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(savedItem(validatedResponse.data));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* sendItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, patchDocument, shareUpn } = payload as {
    id: string;
    patchDocument: JSONPatchDocument;
    shareUpn: string;
  };
  const validatedResponse: ValidatedResponse<SocItem> = yield call(
    [socService, socService.send],
    id,
    patchDocument,
    shareUpn,
    {
      token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(sentItem(validatedResponse.data));
    yield put(refreshActivityFeed(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* sendInternalMessage({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  subject: string;
  message: string;
  recipients: string[];
}>) {
  const { token } = yield select(authSelector);
  const { id, files, subject, message, recipients } = payload;
  try {
    yield call(
      [socService, socService.sendInternalMessage],
      id,
      files,
      subject,
      message,
      recipients,
      { token }
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
    throw err;
  }
}

function* assignItemSaga({
  payload
}: ActionPayload<{
  id: string,
  upn: string,
  displayName: string
}>) {
  const { token } = yield select(authSelector);
  const { id, upn, displayName } = payload;

  try {
    const soc: SocItem = yield call(
      [socService, socService.assign],
      id,
      upn,
      displayName,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(soc));
  } catch (err: any) {
      throw err;
  }
}

function* unassignItemSaga({
  payload
}: ActionPayload<{
  id: string
}>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  try {
    const soc: SocItem = yield call(
      [socService, socService.unassign],
      id,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(soc));
  } catch (err: any) {
      throw err;
  }
}

function* addCaseNoteSaga({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  note: string;
  noteType: number;
}>) {
  const { token } = yield select(authSelector);
  const { id, files, note, noteType } = payload;
  try {
    yield call(
      [socService, socService.addCaseNote],
      id,
      files,
      note,
      noteType,
      { token }
    );
    yield put(refreshActivityFeed(id));
  } catch (err: any) {
      throw err;
  }
}

function* closeSocSaga({
  payload
}: ActionPayload<{ id: string, patchDocument: JSONPatchDocument; }>) {
  const { token } = yield select(authSelector);
  const { id, patchDocument } = payload;

  try {
    const validatedResponse: ValidatedResponse<SocItem> = yield call(
    [socService, socService.close],
    id,
        patchDocument,
    {
      token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(refreshActivityFeed(id));
    yield put(closedSoc(validatedResponse.data));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
  } catch (err: any) {
    throw err;
  }
}

function* reopenSocSaga({
  payload
}: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const soc: SocItem = yield call(
    [socService, socService.reopenSoc],
    id,
    { token }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(soc));
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; message: string }>) {
  const { token } = yield select(authSelector);
  const { id, files, message } = payload;
  try {
    const soc: SocItem = yield call(
      [socService, socService.sendMessage],
      id,
      files,
      message,
      {
        token,
      }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(soc));
  } catch (err: any) {
      throw err;
  }
}

function* sendMultipleMessageSaga({
  payload,
}: ActionPayload<{ files: File[]; message: string, filters: SocMessageFilters }>) {
  const { token } = yield select(authSelector);
  const { files, message, filters } = payload;
  try {
    const response: number = yield call(
      [socService, socService.sendMultipleMessage],
      files,
      message,
      filters,
      {
        token,
      }
    );

    if (response) {
      yield put(sentMultipleMessage(response))
    }
  } catch (err: any) {
      throw err;
  }
}

function* addEventSaga({
  payload,
}: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, newEvent } = payload;

  try {
    const soc: SocItem = yield call(
      [socService, socService.addEvent],
      id,
      newEvent,
      {
        token,
      }
    );

    yield put(refreshActivityFeed(id));
    yield put(loadedItem(soc));
  } catch (err: any) {
    throw err;
  }
}

function* completeEventSaga({
  payload,
}: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, eventId, completedEvent } = payload;

  const soc: SocItem = yield call(
    [socService, socService.completeEvent],
    id,
    eventId,
    completedEvent,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(soc));
}

function* enableResubmitSaga({
  payload
}: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const soc: SocItem = yield call(
    [socService, socService.enableResubmit],
    id,
    { token }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(soc));
}


export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchSendingSaga(),
    watchSendInternalMessageSaga(),
    watchAssignSaga(),
    watchUnassignSaga(),
    watchAddCaseNoteSaga(),
    watchCloseSocSaga(),
    watchReOpenSocSaga(),
    watchSendMessageSaga(),
    watchSendMultipleMessageSaga(),
    watchEventSaga(),
    watchCompleteEventSaga(),
    watchEnableResubmitSaga(),
  ]);
}
